import React, { useState } from "react";
import { useConsultContext } from "../../contexts";
import { Consult } from "../../models";
import { DropdownMenu, DropdownMenuItem, TablePagination } from "../Common";
import { CONSULT_TABLE_PAGE_SIZE, StatusReason } from "../../constants";
import CircularProgress from "@mui/material/CircularProgress";
import { calculateWaitTime, formatPhone } from "../../helpers";
import { CancelConsultModal } from "./CancelConsultModal";
import { RequeuePatientModal } from "./RequeuePatientModal";
import { UpdatePriority, UpdatePriorityModal } from "./UpdatePriorityModal";

const consultMenuItems: DropdownMenuItem[] = [
	{
		label: "Re-queue patient",
		value: "requeue",
		enabled: true,
		iconUrl: "images/svg/single-neutral-actions-refresh.svg"
	},
	{
		label: "Cancel",
		value: "cancel",
		enabled: true,
		iconUrl: "images/svg/cancel-blue.svg"
	},
	{
		label: "Set as priority patient",
		value: "priority",
		enabled: true,
		iconUrl: "images/svg/chevron-up-grey.svg"
	}
];

export const ConsultTable = React.memo(() => {
	const { consults, setRefreshConsults } = useConsultContext();
	const [pagedConsults, setPagedConsults] = useState<Consult[] | undefined>(
		undefined
	);
	const [selectedConsult, setSelectedConsult] = useState<Consult | undefined>(
		undefined
	);
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | undefined>(
		undefined
	);
	const [openConsult, setOpenConsult] = useState<Consult | undefined>(
		undefined
	);
	const [openCancelModal, setOpenCancelModal] = useState(false);
	const [openRequeueModal, setOpenRequeueModal] = useState(false);
	const [openUpdatePriorityModal, setOpenUpdatePriorityModal] = useState(false);

	const rowClassName = (consult: Consult) => {
		if ([1, 2].includes(consult.priority)) {
			return "priority";
		}
		if (consult.statusReason === StatusReason.ASSIGNED) {
			return "assigned";
		}
		if (consult.statusReason === StatusReason.WITHDOCTOR) {
			return "withdoctor";
		}
	};

	const consultIcon = (consult: Consult) => {
		if ([1, 2].includes(consult.priority)) {
			return "road-sign-warning-black.svg";
		}
		switch (consult.statusReason) {
			case StatusReason.WAITING:
			case StatusReason.QUEUED:
				return "time-clock-circle-yellow.svg";
			case StatusReason.ASSIGNED:
				return "check-circle-green.svg";
			case StatusReason.WITHDOCTOR:
				return "telehealth-doctors-selected.svg";
			default:
				return "";
		}
	};

	const getStatusReason = (consult: Consult) => {
		if ([1, 2].includes(consult.priority)) {
			return "Priority";
		}
		switch (consult.statusReason) {
			case StatusReason.QUEUED:
				return StatusReason.WAITING;
			case StatusReason.WITHDOCTOR:
				return "With Doctor";
			default:
				return consult.statusReason;
		}
	};

	const handlePageChange = (consults: Consult[]) => {
		setPagedConsults(consults);
	};

	const renderMenuItems = (consult: Consult) => {
		const isCancelable = !consult.assignedDoctorId;
		const cancelMenuOptionIndex = consultMenuItems.findIndex(
			(menuItem) => menuItem.value === "cancel"
		);
		consultMenuItems[cancelMenuOptionIndex].enabled = isCancelable;
		const isUpdatePriorityAllowed =
			[
				StatusReason.QUEUED.toString(),
				StatusReason.WAITING.toString()
			].includes(consult.statusReason) && consult.priority === 0;
		const updatePriorityMenuOptionIndex = consultMenuItems.findIndex(
			(menuItem) => menuItem.value === "priority"
		);
		consultMenuItems[updatePriorityMenuOptionIndex].enabled =
			isUpdatePriorityAllowed;

		return consultMenuItems;
	};

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLElement>,
		consult: Consult
	) => {
		setMenuAnchorEl(event.currentTarget);
		setOpenConsult(consult);
	};

	const handleMenuClose = (value: string) => {
		if (value) {
			setSelectedConsult(openConsult);
			switch (value) {
				case "requeue": {
					setOpenRequeueModal(true);
					break;
				}
				case "cancel": {
					setOpenCancelModal(true);
					break;
				}
				case "priority": {
					setOpenUpdatePriorityModal(true);
					break;
				}
				default:
					break;
			}
		}
		setMenuAnchorEl(undefined);
		setOpenConsult(undefined);
	};

	const handleCloseCancelConsultModal = (cancelled: boolean) => {
		setOpenCancelModal(false);
		if (cancelled) {
			setSelectedConsult(undefined);
			setRefreshConsults(true);
		}
	};

	const handleCloseRequeuePatientModal = (requeued: boolean) => {
		setOpenRequeueModal(false);
		if (requeued) {
			setSelectedConsult(undefined);
			setRefreshConsults(true);
		}
	};

	const handleCloseUpdatePriorityModal = (updated: boolean) => {
		setOpenUpdatePriorityModal(false);
		if (updated) {
			setSelectedConsult(undefined);
			setRefreshConsults(true);
		}
	};

	return (
		<>
			{consults != null ? (
				<>
					<table className="ch-table w-full">
						<thead>
							<tr>
								<th scope="col">
									<div className="ml-12">Status</div>
								</th>
								<th scope="col">Patient</th>
								<th scope="col">Centre</th>
								<th scope="col">Doctor</th>
								<th scope="col">Contact Number</th>
								<th scope="col">Total Wait Time</th>
								<th scope="col">Telehealth Wait Time</th>
								<th scope="col">Assigned Time</th>
								<th scope="col">Comment</th>
								<th scope="col">Actions</th>
							</tr>
						</thead>
						<tbody>
							{pagedConsults?.map((consult, index) => (
								<tr key={consult.id} className={rowClassName(consult)}>
									<td className={rowClassName(consult)}>
										<img
											src={`images/svg/${consultIcon(consult)}`}
											width="20"
											height="20"
											className="ms-2 me-2"
											alt="status"
										/>
										{getStatusReason(consult)}
									</td>
									<td>{`${consult?.firstName} ${consult?.lastName}`}</td>
									<td>{consult.clinicName}</td>
									<td>{consult.assignedDoctorName || ""}</td>
									<td>{formatPhone(consult.patientPhone.trim())}</td>
									<td>{calculateWaitTime(consult.arrivalDateTime)}</td>
									<td>
										{consult.telehealthArrivalDateTime
											? calculateWaitTime(consult.telehealthArrivalDateTime)
											: ""}
									</td>
									<td>
										{" "}
										{consult.assignedDateTime &&
											calculateWaitTime(consult.assignedDateTime)}
									</td>
									<td>{consult.comment}</td>
									<td align="center">
										<div
											className="flex"
											style={{ cursor: "pointer" }}
											onClick={(e) => handleMenuOpen(e, consult)}
										>
											<img
												src="images/svg/vertical-dot-menu.svg"
												alt="available actions"
												className="mr-4"
											/>
											Actions
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<TablePagination
						items={consults || []}
						pageSize={CONSULT_TABLE_PAGE_SIZE}
						pageChange={handlePageChange}
					/>
					{openConsult && (
						<DropdownMenu
							anchorEl={menuAnchorEl!}
							open={Boolean(menuAnchorEl)}
							menuItems={renderMenuItems(openConsult)}
							handleClose={handleMenuClose}
						/>
					)}
				</>
			) : (
				<div className="flex flex-col justify-center items-center">
					<CircularProgress size={60} />
				</div>
			)}
			{consults == null ||
				(consults.length === 0 && (
					<div className="flex justify-center ch-xl-text ch-em-text-navy mt-12">
						There are no telehealth consults
					</div>
				))}

			{selectedConsult != null && (
				<CancelConsultModal
					consult={selectedConsult}
					open={openCancelModal}
					onClose={handleCloseCancelConsultModal}
				/>
			)}
			{selectedConsult != null && (
				<RequeuePatientModal
					consult={selectedConsult}
					open={openRequeueModal}
					onClose={handleCloseRequeuePatientModal}
				/>
			)}
			{selectedConsult != null && (
				<UpdatePriorityModal
					open={openUpdatePriorityModal}
					consult={selectedConsult}
					action={UpdatePriority.Prioritise}
					close={handleCloseUpdatePriorityModal}
				/>
			)}
		</>
	);
});
